/**
 * Parse the email-draft blocks the CS agent emits inside its assistant
 * messages (`<to>`, `<subject>`, `<html>`, `<plaintext>`, `<rationale>`).
 *
 * The agent's protocol (chat_service.SYSTEM_PROMPT) mandates these blocks
 * verbatim when drafting. We strip them from the rendered chat bubble — they
 * read poorly as raw text — and surface the structured fields via the
 * "Open email composer" affordance.
 */

export interface EmailDraft {
  to: string;
  subject: string;
  html: string;
  plainText: string;
  rationale: string | null;
}

export interface ParsedAssistantContent {
  /** The agent's content with email blocks removed; safe to render as markdown. */
  visibleText: string;
  /** Parsed draft, only present when *all four* required blocks (to/subject/html/plaintext) parsed. */
  draft: EmailDraft | null;
}

const BLOCK_PATTERNS: Record<keyof EmailDraft, RegExp> = {
  to: /<to>\s*([\s\S]*?)\s*<\/to>/i,
  subject: /<subject>\s*([\s\S]*?)\s*<\/subject>/i,
  html: /<html>\s*([\s\S]*?)\s*<\/html>/i,
  plainText: /<plaintext>\s*([\s\S]*?)\s*<\/plaintext>/i,
  rationale: /<rationale>\s*([\s\S]*?)\s*<\/rationale>/i,
};

/**
 * The set of regex patterns whose matched substrings (including the tags)
 * should be removed from the visible chat bubble. `g` flag so a multi-draft
 * turn (e.g. agent emitted drafts for two users) doesn't leave a trailing
 * raw `<to>...</to>` block as literal text. We strip rationale too — the
 * modal will surface it; double-rendering in the bubble adds noise.
 */
const STRIP_PATTERNS: RegExp[] = [
  /<to>[\s\S]*?<\/to>/gi,
  /<subject>[\s\S]*?<\/subject>/gi,
  /<html>[\s\S]*?<\/html>/gi,
  /<plaintext>[\s\S]*?<\/plaintext>/gi,
  /<rationale>[\s\S]*?<\/rationale>/gi,
];

export function parseEmailDraft(content: string): ParsedAssistantContent {
  if (!content) {
    return { visibleText: '', draft: null };
  }

  const to = matchBlock(content, BLOCK_PATTERNS.to);
  const subject = matchBlock(content, BLOCK_PATTERNS.subject);
  const html = matchBlock(content, BLOCK_PATTERNS.html);
  const plainText = matchBlock(content, BLOCK_PATTERNS.plainText);
  const rationale = matchBlock(content, BLOCK_PATTERNS.rationale);

  // Only treat this as a draft if the four required pieces are present.
  // (Rationale is optional and may legitimately be missing on edits.)
  const isDraft = Boolean(to && subject && html && plainText);

  let visibleText = content;
  if (isDraft) {
    for (const pattern of STRIP_PATTERNS) {
      visibleText = visibleText.replace(pattern, '');
    }
    // Collapse the blank-line trail the strip leaves behind.
    visibleText = visibleText.replace(/\n{3,}/g, '\n\n').trim();
  }

  return {
    visibleText,
    draft: isDraft
      ? {
          to: to as string,
          subject: subject as string,
          html: html as string,
          plainText: plainText as string,
          rationale,
        }
      : null,
  };
}

function matchBlock(content: string, pattern: RegExp): string | null {
  const match = content.match(pattern);
  if (!match || !match[1]) return null;
  const value = match[1].trim();
  return value.length > 0 ? value : null;
}
